import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  LayoutDashboard, 
  FileText, 
  LogOut, 
  Plus, 
  Edit2, 
  Trash2, 
  Menu,
  X,
  Eye,
  EyeOff,
  Save,
  Loader2,
  Home,
  Info,
  Phone,
  Settings,
  Users,
  Layers,
  GripVertical,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [blogs, setBlogs] = useState([]);
  const [sections, setSections] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSectionDialogOpen, setIsSectionDialogOpen] = useState(false);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState('homepage');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [blogForm, setBlogForm] = useState({
    title: '',
    subtitle: '',
    excerpt: '',
    content: '',
    category: '',
    image: '',
    tags: '',
    date: new Date().toISOString().split('T')[0],
    readTime: '5 min read',
    published: true
  });

  const [sectionForm, setSectionForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    badge: '',
    buttonText: '',
    buttonLink: '',
    secondaryButtonText: '',
    secondaryButtonLink: '',
    backgroundImage: ''
  });

  const [itemForm, setItemForm] = useState({
    icon: '',
    title: '',
    description: '',
    value: '',
    label: '',
    image: '',
    link: '',
    tag: '',
    color: '',
    order: 0
  });

  const fetchBlogs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blogs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBlogs(data);
      }
    } catch (error) {
      toast.error('Failed to fetch blogs');
    }
  }, [API_URL, token]);

  const fetchSections = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSections(data);
      }
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    }
  }, [API_URL, token]);

  const fetchContactInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contact`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch contact info:', error);
    }
  }, [API_URL, token]);

  const initializeContent = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/admin/init-content`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Content initialized successfully');
        await fetchSections();
        await fetchContactInfo();
      }
    } catch (error) {
      toast.error('Failed to initialize content');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBlogs(), fetchSections(), fetchContactInfo()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBlogs, fetchSections, fetchContactInfo]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Blog functions
  const resetBlogForm = () => {
    setBlogForm({
      title: '', subtitle: '', excerpt: '', content: '', category: '',
      image: '', tags: '', date: new Date().toISOString().split('T')[0],
      readTime: '5 min read', published: true
    });
    setEditingBlog(null);
  };

  const openCreateBlogDialog = () => {
    resetBlogForm();
    setIsDialogOpen(true);
  };

  const openEditBlogDialog = (blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      subtitle: blog.subtitle || '',
      excerpt: blog.excerpt,
      content: blog.content,
      category: blog.category,
      image: blog.image,
      tags: blog.tags?.join(', ') || '',
      date: blog.date,
      readTime: blog.readTime,
      published: blog.published
    });
    setIsDialogOpen(true);
  };

  const handleSaveBlog = async () => {
    setSaving(true);
    try {
      const blogData = {
        ...blogForm,
        tags: blogForm.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      };

      const url = editingBlog 
        ? `${API_URL}/api/admin/blogs/${editingBlog.id}`
        : `${API_URL}/api/admin/blogs`;
      
      const method = editingBlog ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(blogData)
      });

      if (response.ok) {
        toast.success(editingBlog ? 'Blog updated' : 'Blog created');
        setIsDialogOpen(false);
        resetBlogForm();
        fetchBlogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save blog');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!window.confirm('Delete this blog?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/blogs/${blogId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Blog deleted');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Section functions
  const openEditSectionDialog = (section) => {
    setEditingSection(section);
    setSectionForm({
      title: section.title || '',
      subtitle: section.subtitle || '',
      description: section.description || '',
      badge: section.badge || '',
      buttonText: section.buttonText || '',
      buttonLink: section.buttonLink || '',
      secondaryButtonText: section.secondaryButtonText || '',
      secondaryButtonLink: section.secondaryButtonLink || '',
      backgroundImage: section.backgroundImage || ''
    });
    setIsSectionDialogOpen(true);
  };

  const handleSaveSection = async () => {
    if (!editingSection) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/sections/${editingSection.page}/${editingSection.sectionKey}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(sectionForm)
        }
      );
      if (response.ok) {
        toast.success('Section updated');
        setIsSectionDialogOpen(false);
        fetchSections();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Item functions
  const openAddItemDialog = (section) => {
    setEditingSection(section);
    setEditingItem(null);
    setItemForm({
      icon: '', title: '', description: '', value: '', label: '',
      image: '', link: '', tag: '', color: '', order: section.items?.length || 0
    });
    setIsItemDialogOpen(true);
  };

  const openEditItemDialog = (section, item) => {
    setEditingSection(section);
    setEditingItem(item);
    setItemForm({
      icon: item.icon || '',
      title: item.title || '',
      description: item.description || '',
      value: item.value || '',
      label: item.label || '',
      image: item.image || '',
      link: item.link || '',
      tag: item.tag || '',
      color: item.color || '',
      order: item.order || 0
    });
    setIsItemDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!editingSection) return;
    setSaving(true);
    try {
      const url = editingItem
        ? `${API_URL}/api/admin/sections/${editingSection.page}/${editingSection.sectionKey}/items/${editingItem.id}`
        : `${API_URL}/api/admin/sections/${editingSection.page}/${editingSection.sectionKey}/items`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(itemForm)
      });
      if (response.ok) {
        toast.success(editingItem ? 'Item updated' : 'Item added');
        setIsItemDialogOpen(false);
        fetchSections();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (section, itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/sections/${section.page}/${section.sectionKey}/items/${itemId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        toast.success('Item deleted');
        fetchSections();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Contact functions
  const handleSaveContact = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/contact`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(contactInfo)
      });
      if (response.ok) {
        toast.success('Contact info updated');
        fetchContactInfo();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const pageSections = sections.filter(s => s.page === selectedPage);

  const pages = [
    { id: 'homepage', label: 'Homepage', icon: Home },
    { id: 'about', label: 'About', icon: Info },
    { id: 'gramtek', label: 'Gramtek', icon: Layers },
    { id: 'contact', label: 'Contact', icon: Phone }
  ];

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'pages', label: 'Page Content', icon: Layers },
    { id: 'blogs', label: 'Blogs', icon: FileText },
    { id: 'contact', label: 'Contact Info', icon: Phone }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-0 md:w-20'} bg-slate-800 border-r border-slate-700 transition-all duration-300 overflow-hidden flex flex-col`}>
        <div className="p-4 border-b border-slate-700">
          <div className={`flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
            {sidebarOpen && <h1 className="text-xl font-bold text-white">Admin CMS</h1>}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white hidden md:flex"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        
        <nav className="p-4 space-y-2 flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={activeTab === item.id ? 'secondary' : 'ghost'}
                className={`w-full justify-start ${!sidebarOpen && 'justify-center px-2'} text-slate-300 hover:text-white hover:bg-slate-700`}
                onClick={() => setActiveTab(item.id)}
              >
                <Icon className="w-5 h-5" />
                {sidebarOpen && <span className="ml-3">{item.label}</span>}
              </Button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <Button
            variant="ghost"
            className={`w-full justify-start ${!sidebarOpen && 'justify-center px-2'} text-red-400 hover:text-red-300 hover:bg-red-500/10`}
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="md:hidden bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
          <h1 className="text-lg font-bold text-white">Admin CMS</h1>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400">
            <Menu className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Dashboard Overview</h2>
                {sections.length === 0 && (
                  <Button onClick={initializeContent} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Initialize Content
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-slate-400 text-sm">Total Blogs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-cyan-400">{blogs.length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-slate-400 text-sm">Page Sections</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-blue-400">{sections.length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-slate-400 text-sm">Published</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-green-400">{blogs.filter(b => b.published).length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-slate-400 text-sm">Drafts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-amber-400">{blogs.filter(b => !b.published).length}</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button onClick={() => setActiveTab('blogs')} className="bg-slate-700 hover:bg-slate-600 h-auto py-4">
                    <div className="text-left">
                      <FileText className="w-6 h-6 mb-2 text-cyan-400" />
                      <div className="font-semibold">Manage Blogs</div>
                      <div className="text-xs text-slate-400">Create, edit, delete posts</div>
                    </div>
                  </Button>
                  <Button onClick={() => setActiveTab('pages')} className="bg-slate-700 hover:bg-slate-600 h-auto py-4">
                    <div className="text-left">
                      <Layers className="w-6 h-6 mb-2 text-blue-400" />
                      <div className="font-semibold">Edit Pages</div>
                      <div className="text-xs text-slate-400">Update website content</div>
                    </div>
                  </Button>
                  <Button onClick={() => setActiveTab('contact')} className="bg-slate-700 hover:bg-slate-600 h-auto py-4">
                    <div className="text-left">
                      <Phone className="w-6 h-6 mb-2 text-green-400" />
                      <div className="font-semibold">Contact Info</div>
                      <div className="text-xs text-slate-400">Update contact details</div>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Page Content Management</h2>
                {sections.length === 0 && (
                  <Button onClick={initializeContent} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
                    Initialize Content
                  </Button>
                )}
              </div>

              <Tabs value={selectedPage} onValueChange={setSelectedPage}>
                <TabsList className="bg-slate-800 border border-slate-700">
                  {pages.map((page) => {
                    const Icon = page.icon;
                    return (
                      <TabsTrigger 
                        key={page.id} 
                        value={page.id}
                        className="data-[state=active]:bg-cyan-500 data-[state=active]:text-white"
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {page.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {pages.map((page) => (
                  <TabsContent key={page.id} value={page.id} className="mt-6">
                    {pageSections.length === 0 ? (
                      <Card className="bg-slate-800 border-slate-700">
                        <CardContent className="py-12 text-center">
                          <Layers className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                          <p className="text-slate-400">No sections found for this page</p>
                          <p className="text-slate-500 text-sm">Click "Initialize Content" to create default sections</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {pageSections.map((section) => (
                          <Card key={section.id} className="bg-slate-800 border-slate-700">
                            <CardHeader className="pb-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                                    <Settings className="w-5 h-5 text-cyan-400" />
                                  </div>
                                  <div>
                                    <CardTitle className="text-white text-lg capitalize">
                                      {section.sectionKey.replace(/-/g, ' ')}
                                    </CardTitle>
                                    <p className="text-slate-500 text-sm">{section.title || 'No title set'}</p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditSectionDialog(section)}
                                  className="text-cyan-400 hover:text-cyan-300"
                                >
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit Section
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              {section.description && (
                                <p className="text-slate-400 text-sm mb-4 line-clamp-2">{section.description}</p>
                              )}
                              
                              {section.items && section.items.length > 0 && (
                                <div className="mt-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-slate-300">Items ({section.items.length})</h4>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openAddItemDialog(section)}
                                      className="text-green-400 hover:text-green-300"
                                    >
                                      <Plus className="w-4 h-4 mr-1" />
                                      Add Item
                                    </Button>
                                  </div>
                                  <div className="space-y-2">
                                    {section.items.sort((a, b) => a.order - b.order).map((item) => (
                                      <div 
                                        key={item.id}
                                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg group"
                                      >
                                        <div className="flex items-center gap-3">
                                          <GripVertical className="w-4 h-4 text-slate-500" />
                                          <div>
                                            <p className="text-white text-sm font-medium">
                                              {item.title || (item.value && item.label ? `${item.value} - ${item.label}` : item.value || item.label || 'Untitled')}
                                            </p>
                                            {item.description && (
                                              <p className="text-slate-400 text-xs line-clamp-1">{item.description}</p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => openEditItemDialog(section, item)}
                                            className="h-8 w-8 text-slate-400 hover:text-cyan-400"
                                          >
                                            <Edit2 className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteItem(section, item.id)}
                                            className="h-8 w-8 text-slate-400 hover:text-red-400"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {(!section.items || section.items.length === 0) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openAddItemDialog(section)}
                                  className="mt-2 border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add First Item
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          )}

          {/* Blogs Tab */}
          {activeTab === 'blogs' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Manage Blogs</h2>
                <Button onClick={openCreateBlogDialog} className="bg-cyan-500 hover:bg-cyan-600">
                  <Plus className="w-4 h-4 mr-2" /> New Blog
                </Button>
              </div>

              {blogs.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="w-12 h-12 text-slate-600 mb-4" />
                    <p className="text-slate-400 text-lg">No blogs yet</p>
                    <p className="text-slate-500 text-sm">Create your first blog post</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {blogs.map((blog) => (
                    <Card key={blog.id} className="bg-slate-800 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-white truncate">{blog.title}</h3>
                              {blog.published ? (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                                  <Eye className="w-3 h-3" /> Published
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                                  <EyeOff className="w-3 h-3" /> Draft
                                </span>
                              )}
                            </div>
                            <p className="text-slate-400 text-sm line-clamp-2">{blog.excerpt}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>{blog.category}</span>
                              <span>{blog.date}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditBlogDialog(blog)}
                              className="text-slate-400 hover:text-cyan-400"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteBlog(blog.id)}
                              className="text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && contactInfo && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Contact Information</h2>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Company Name</Label>
                      <Input
                        value={contactInfo.companyName || ''}
                        onChange={(e) => setContactInfo({...contactInfo, companyName: e.target.value})}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Email</Label>
                      <Input
                        value={contactInfo.email || ''}
                        onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        value={contactInfo.phone || ''}
                        onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Address</Label>
                      <Textarea
                        value={contactInfo.address || ''}
                        onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})}
                        className="bg-slate-900 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">Benefits (one per line)</Label>
                    <Textarea
                      value={contactInfo.benefits?.join('\n') || ''}
                      onChange={(e) => setContactInfo({...contactInfo, benefits: e.target.value.split('\n').filter(Boolean)})}
                      className="bg-slate-900 border-slate-600 text-white"
                      rows={4}
                      placeholder="Proven track record with 350+ villages served"
                    />
                  </div>

                  <Button onClick={handleSaveContact} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Blog Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingBlog ? 'Edit Blog' : 'Create New Blog'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={blogForm.title}
                  onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                  placeholder="Blog title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Input
                  value={blogForm.category}
                  onChange={(e) => setBlogForm({...blogForm, category: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                  placeholder="e.g., Case Study"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Excerpt</Label>
              <Textarea
                value={blogForm.excerpt}
                onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Content</Label>
              <Textarea
                value={blogForm.content}
                onChange={(e) => setBlogForm({...blogForm, content: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white min-h-[150px]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Image URL</Label>
                <Input
                  value={blogForm.image}
                  onChange={(e) => setBlogForm({...blogForm, image: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Tags (comma separated)</Label>
                <Input
                  value={blogForm.tags}
                  onChange={(e) => setBlogForm({...blogForm, tags: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={blogForm.published}
                onCheckedChange={(checked) => setBlogForm({...blogForm, published: checked})}
              />
              <Label className="text-slate-300">Published</Label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleSaveBlog} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingBlog ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Section Dialog */}
      <Dialog open={isSectionDialogOpen} onOpenChange={setIsSectionDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white capitalize">
              Edit {editingSection?.sectionKey?.replace(/-/g, ' ')} Section
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Badge Text</Label>
              <Input
                value={sectionForm.badge}
                onChange={(e) => setSectionForm({...sectionForm, badge: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
                placeholder="e.g., About Us"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input
                value={sectionForm.title}
                onChange={(e) => setSectionForm({...sectionForm, title: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Subtitle</Label>
              <Input
                value={sectionForm.subtitle}
                onChange={(e) => setSectionForm({...sectionForm, subtitle: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={sectionForm.description}
                onChange={(e) => setSectionForm({...sectionForm, description: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Button Text</Label>
                <Input
                  value={sectionForm.buttonText}
                  onChange={(e) => setSectionForm({...sectionForm, buttonText: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Button Link</Label>
                <Input
                  value={sectionForm.buttonLink}
                  onChange={(e) => setSectionForm({...sectionForm, buttonLink: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Background Image URL</Label>
              <Input
                value={sectionForm.backgroundImage}
                onChange={(e) => setSectionForm({...sectionForm, backgroundImage: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsSectionDialogOpen(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleSaveSection} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Section
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={isItemDialogOpen} onOpenChange={setIsItemDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={itemForm.title}
                  onChange={(e) => setItemForm({...itemForm, title: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Icon (Lucide name)</Label>
                <Input
                  value={itemForm.icon}
                  onChange={(e) => setItemForm({...itemForm, icon: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                  placeholder="e.g., Users, Target, Zap"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={itemForm.description}
                onChange={(e) => setItemForm({...itemForm, description: e.target.value})}
                className="bg-slate-900 border-slate-600 text-white"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Value (for metrics)</Label>
                <Input
                  value={itemForm.value}
                  onChange={(e) => setItemForm({...itemForm, value: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                  placeholder="e.g., 100,000+"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Label</Label>
                <Input
                  value={itemForm.label}
                  onChange={(e) => setItemForm({...itemForm, label: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Image URL</Label>
                <Input
                  value={itemForm.image}
                  onChange={(e) => setItemForm({...itemForm, image: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Link</Label>
                <Input
                  value={itemForm.link}
                  onChange={(e) => setItemForm({...itemForm, link: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Tag</Label>
                <Input
                  value={itemForm.tag}
                  onChange={(e) => setItemForm({...itemForm, tag: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Order</Label>
                <Input
                  type="number"
                  value={itemForm.order}
                  onChange={(e) => setItemForm({...itemForm, order: parseInt(e.target.value) || 0})}
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsItemDialogOpen(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleSaveItem} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingItem ? 'Update' : 'Add'} Item
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
